import { useState, useMemo } from 'react';
import {
  Container,
  Box,
  Flex,
  Heading,
  Text,
  useColorModeValue,
  Spinner,
} from '@chakra-ui/react';
import { CalendarIcon } from '@chakra-ui/icons';
import { format } from 'date-fns';
import { useAuth } from '../../../contexts/AuthContext';
import { useCalendarActionItems } from './useCalendarActionItems';
import { useCalendarMilestones } from './useCalendarMilestones';
import { useUsers } from '../Users/useUsers';
import { getDateRangeForView, navigatePrevious, navigateNext } from './calendar.util';
import { CalendarHeader } from './components/CalendarHeader';
import { CalendarNavigation } from './components/CalendarNavigation';
import { CalendarMonthView } from './components/CalendarMonthView';
import { CalendarWeekView } from './components/CalendarWeekView';
import { CalendarDayView } from './components/CalendarDayView';

type ViewType = 'month' | 'week' | 'day';

/**
 * Calendar Page - Main page for viewing action items and milestones in calendar format
 * Migrated from Supabase/Tailwind to Firestore/Chakra UI
 */
export function CalendarPage() {
  const { currentUser } = useAuth();
  const { users } = useUsers();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<ViewType>('month');
  const [selectedUser, setSelectedUser] = useState<string>('all');

  // Calculate date range based on current view - memoize to prevent infinite renders
  const { startDate, endDate } = useMemo(
    () => getDateRangeForView(currentDate, view),
    [currentDate, view]
  );

  // Fetch action items and milestones for the date range
  const { actionItems, loading: actionItemsLoading } = useCalendarActionItems({
    startDate,
    endDate,
    selectedUserId: selectedUser !== 'all' ? selectedUser : undefined,
  });

  const { milestones, loading: milestonesLoading } = useCalendarMilestones({
    startDate,
    endDate,
    selectedUserId: selectedUser !== 'all' ? selectedUser : undefined,
  });

  const loading = actionItemsLoading || milestonesLoading;

  // Color mode values
  const subheadingColor = useColorModeValue('gray.600', 'gray.400');
  const bgColor = useColorModeValue('white', 'gray.800');

  const handleNavigatePrevious = () => {
    setCurrentDate(navigatePrevious(currentDate, view));
  };

  const handleNavigateNext = () => {
    setCurrentDate(navigateNext(currentDate, view));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const getHeaderText = () => {
    if (view === 'month') {
      return format(currentDate, 'MMMM yyyy');
    }
    if (view === 'week') {
      const { startDate: weekStart } = getDateRangeForView(currentDate, 'week');
      return `Week of ${format(weekStart, 'MMM d, yyyy')}`;
    }
    return format(currentDate, 'EEEE, MMMM d, yyyy');
  };

  return (
    <Container maxW="container.xl" py={8}>
      <Box mb={6}>
        <Flex align="center" gap={2} mb={2}>
          <CalendarIcon w={6} h={6} color="blue.500" />
          <Heading size="lg">Calendar</Heading>
        </Flex>
        <Text color={subheadingColor}>
          View your action items and milestones in calendar format
        </Text>
      </Box>

      {/* Header with filters */}
      <CalendarHeader
        view={view}
        onViewChange={setView}
        selectedUser={selectedUser}
        onUserChange={setSelectedUser}
        users={users}
        currentUserId={currentUser?.uid}
      />

      {/* Navigation */}
      <CalendarNavigation
        headerText={getHeaderText()}
        onPrevious={handleNavigatePrevious}
        onNext={handleNavigateNext}
        onToday={handleToday}
      />

      {/* Calendar Content */}
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          py={12}
          bg={bgColor}
          borderRadius="md"
        >
          <Spinner size="xl" />
          <Text ml={4}>Loading calendar...</Text>
        </Box>
      ) : (
        <Box>
          {view === 'month' && (
            <CalendarMonthView
              currentDate={currentDate}
              actionItems={actionItems}
              milestones={milestones}
            />
          )}
          {view === 'week' && (
            <CalendarWeekView
              currentDate={currentDate}
              actionItems={actionItems}
              milestones={milestones}
            />
          )}
          {view === 'day' && (
            <CalendarDayView
              currentDate={currentDate}
              actionItems={actionItems}
              milestones={milestones}
            />
          )}
        </Box>
      )}
    </Container>
  );
}

